import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/logo.webp";
import { Button } from "@/components/ui/button";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import CartButton from "@/components/cart/CartButton";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, signOut, loading, isAdmin, isStaff, isInstructor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getAuthUrl = () => {
    const excludedPaths = ['/auth', '/reset-password', '/accept-invite'];
    const isExcluded = excludedPaths.some(p => location.pathname.startsWith(p));
    if (isExcluded) return '/auth';
    return `/auth?returnTo=${encodeURIComponent(location.pathname + location.search)}`;
  };

  // Determine the appropriate dashboard route based on user roles
  const getDashboardRoute = () => {
    if (isAdmin) return "/admin";
    if (isStaff) return "/staff";
    if (isInstructor) return "/instructor";
    return "/dashboard"; // Default to parent dashboard
  };

  const handleDashboardClick = () => {
    navigate(getDashboardRoute());
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/50">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img src={logo} alt="MSB Camps" width="128" height="128" className="h-14 w-auto" fetchPriority="high" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          <Link to="/">
            <Button variant="ghost" className="font-semibold text-foreground text-base">
              Home
            </Button>
          </Link>

          <Link to="/after-school">
            <Button variant="ghost" className="font-semibold text-foreground text-base">
              After School
            </Button>
          </Link>

          <Link to="/early-care">
            <Button variant="ghost" className="font-semibold text-foreground text-base">
              Early Care
            </Button>
          </Link>

          <Link to="/clubs">
            <Button variant="ghost" className="font-semibold text-foreground text-base">
              Clubs
            </Button>
          </Link>

          <Link to="/holiday-camps">
            <Button variant="ghost" className="font-semibold text-foreground text-base">
              Holiday Break Camps
            </Button>
          </Link>

          <Link to="/summer-camps">
            <Button variant="ghost" className="font-semibold text-foreground text-base">
              Summer Camps
            </Button>
          </Link>

        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          {!loading && (
            user ? (
              <>
                <CartButton />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="lg" className="font-bold rounded-full gap-2">
                      <User className="w-4 h-4" />
                      My Account
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-popover z-50">
                    <DropdownMenuItem onClick={handleDashboardClick} className="font-medium cursor-pointer">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={signOut} className="font-medium cursor-pointer text-destructive">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to={getAuthUrl()}>
                  <Button variant="ghost" size="lg" className="font-bold">
                    Sign In
                  </Button>
                </Link>
                <Link to="/programs">
                  <Button size="lg" className="font-bold rounded-full px-8">
                    Register
                  </Button>
                </Link>
              </>
            )
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation"
        >
          {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
        </Button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <nav id="mobile-navigation" aria-label="Mobile" className="lg:hidden bg-background border-t border-border px-4 py-6 space-y-4">
          <div className="space-y-2">
            <Link to="/" className="block py-2 font-medium text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
              Home
            </Link>
            <Link to="/after-school" className="block py-2 font-medium text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
              After School
            </Link>
            <Link to="/early-care" className="block py-2 font-medium text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
              Early Care
            </Link>
            <Link to="/clubs" className="block py-2 font-medium text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
              Clubs
            </Link>
            <Link to="/holiday-camps" className="block py-2 font-medium text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
              Holiday Break Camps
            </Link>
            <Link to="/summer-camps" className="block py-2 font-medium text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
              Summer Camps
            </Link>
          </div>
          <div className="pt-4 space-y-2">
            {!loading && (
              user ? (
                <>
                  <CartButton className="w-full" size="lg" />
                  <Button 
                    variant="outline" 
                    className="w-full font-bold rounded-full gap-2" 
                    size="lg"
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleDashboardClick();
                    }}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Button>
                  <Button onClick={signOut} variant="ghost" className="w-full font-bold" size="lg">
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Link to={getAuthUrl()}>
                    <Button variant="outline" className="w-full font-bold rounded-full" size="lg">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/programs">
                    <Button className="w-full font-bold rounded-full" size="lg">
                      Register
                    </Button>
                  </Link>
                </>
              )
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
